import { type ReactNode } from "react";

import { useTpaBalanceSession } from "~/hec/tpa/use-tpa-balance-session";
import { useRootLoaderData } from "~/root";

import { useFailSoftResourceState } from "./hooks/use-failsoft-resource";
import { TpaBalanceContext, type TpaBalancePayload } from "./tpa-balance";

const useTpaBalanceResource = (href: string | null, reloadKey: number) =>
  useFailSoftResourceState<TpaBalancePayload>(href, reloadKey);

/**
 * Owns the single TPA-balance fetch for the whole page (SFCC-11869).
 *
 * Replaces the old shared `"tpa-balance"` fetcher key. That key existed to
 * dedupe the slow (>30s) MuleSoft call across its two simultaneous consumers —
 * the header balance bar and the welcome modal — but a `fetcher.load()`
 * rejection is THROWN to the owning route's boundary, and neither `layout.tsx`
 * nor `cart.tsx` exports one, so it reached RootErrorBoundary and blanked the
 * storefront. The rejection that actually fired was the abort the browser
 * issues when the shopper taps "Continue to Checkout" mid-fetch: a
 * cancellation, not a fault. Owning the fetch here keeps the dedupe (one
 * provider, N consumers) while making that abort a no-op — the page the shopper
 * is leaving stays exactly as it was.
 *
 * The fetched balance belongs to ONE shopper (COM-674): the identity reset and
 * the forced-refresh-after-login policy live in `useTpaBalanceSession`
 * (`app/hec/tpa/`). Identity is read from the root loader (`customer` is null
 * for guests) rather than `useAuth`, because the checkout layout mounts this
 * provider outside `AuthProvider`.
 */
export function TpaBalanceProvider({ children }: { children: ReactNode }) {
  const { customer } = useRootLoaderData();
  const value = useTpaBalanceSession(
    customer?.customerId ?? null,
    useTpaBalanceResource,
  );

  return (
    <TpaBalanceContext.Provider value={value}>
      {children}
    </TpaBalanceContext.Provider>
  );
}
